// frontend/src/ts/protocols/persistence/browserUploadStore.ts
import type {BrowserUploadShape, UploadStore} from './uploadStore'
import type {
    GetPendingUploadsArgs,
    StartUploadWorkerConfig,
    StartDataCollectionConfig,
    StartBrowserUploadArgs,
    StartBrowserUploadResult,
    FinalizeBrowserUploadArgs,
    AbortBrowserUploadArgs,
} from './uploadStore'
import type {PendingUpload, UploadSummary, DataCollectionStatus} from './uploadTypes'

import {requestRawFileUpload, uploadRawFile} from '@/common/services/rawfilesService'
import {associateProtocolStepFiles, associateProtocolFiles} from '@/protocols/services/protocols'

interface PendingBrowserUpload {
    filename: string
    file: File
    protocolStepId?: string
}

/**
 * Browser-mode upload store. The browser cannot give Rust an absolute
 * path, so this store does NOT go through the `pending_uploads` queue
 * — it directly requests a pre-signed URL, XHR PUTs the file to S3,
 * and associates it with the protocol step. The wrapping `start /
 * finalize / abort` methods (this class implements `BrowserUploadShape`)
 * are still called by `uploadCoordinator.uploadFileInBrowser` for
 * symmetry with the old streaming contract — but `writeBrowserUploadChunk`
 * has been removed; there is no chunk loop anymore.
 */
export class BrowserUploadStore implements UploadStore {
    // The browser store no longer buffers chunks in memory. We keep a
    // reference to the original File so finalizeBrowserUpload can hand
    // it to uploadRawFile, which uses XMLHttpRequest.send(file) under
    // the hood to stream to S3 without loading the whole file into
    // memory. Memory usage is bounded by the XHR upload chunk size
    // (typically 16 KB) regardless of file size.
    private pendingUploads: Map<string, PendingBrowserUpload> = new Map()

    // Browser-mode-only subclass of the upload surface (start/finalize/
    // abort). TauriUploadStore does NOT implement these.
    browserUpload: BrowserUploadShape = {
        startBrowserUpload: (args) => this.startBrowserUpload(args),
        finalizeBrowserUpload: (args) => this.finalizeBrowserUpload(args),
        abortBrowserUpload: (args) => this.abortBrowserUpload(args),
    }

    async startBrowserUpload(args: StartBrowserUploadArgs): Promise<StartBrowserUploadResult> {
        const uploadId = `${Date.now()}-${Math.floor(Math.random() * 10000)}`
        this.pendingUploads.set(uploadId, {
            filename: args.filename,
            file: args.file,
            protocolStepId: args.protocolStepId,
        })
        return {uploadId}
    }

    async finalizeBrowserUpload(args: FinalizeBrowserUploadArgs): Promise<number> {
        const entry = this.pendingUploads.get(args.uploadId)
        if (!entry) throw new Error(`unknown uploadId: ${args.uploadId}`)
        this.pendingUploads.delete(args.uploadId)

        if (args.file.size !== args.fileSize) {
            throw new Error(
                `size mismatch: file ${args.file.size} !== expected ${args.fileSize}`,
            )
        }


        // Silent: the caller owns the error toast (addStepFiles / the
        // coordinator retry path). Without this, the apiClient interceptor
        // would ALSO pop a generic "Network Error" toast — two popups per
        // failed file. The signal lets Cancel abort the real requests.
        const requestConfig = {silent: true, signal: args.signal}

        // 1. Get pre-signed URL.
        const fileStatus = await requestRawFileUpload(
            args.companyId,
            {name: args.filename, size: args.fileSize} as never,
            {
                filename: args.filename,
                subject_id: args.subjectId,
                metadata: {},
                size: args.fileSize,
                tags: [],
            } as never,
            requestConfig,
        )

        // 2. Stream the file to S3 via uploadRawFile (axios with
        //    onUploadProgress; uses XMLHttpRequest.send(file) under the
        //    hood which streams without loading the whole file into
        //    memory). Replaces the previous raw fetch() call so we use
        //    the same service function the rest of the app uses.
        //
        //    The progress event is forwarded to the caller via
        //    args.onProgress (the streaming coordinator passes a
        //    callback that updates the useUploadStatusStore.browserUploads
        //    slice). Tauri mode does NOT use this path; it routes
        //    uploads through enqueuePickedFiles / pickFile -> enqueue_picked_file
        //    where the worker reports progress via polling.
        await uploadRawFile(
            args.companyId,
            fileStatus,
            args.file as never,
            (progressEvent) => {
                const total = progressEvent.total ?? args.fileSize
                args.onProgress?.(progressEvent.loaded, total)
            },
            requestConfig,
        )

        // 3. Associate the file with the protocol step (if stepId
        //    provided). The file is now linked to the step's file list.
        if (fileStatus.file?.id) {
            if (args.protocolStepId) {
                await associateProtocolStepFiles(
                    args.companyId,
                    args.protocolId,
                    args.protocolStepId,
                    fileStatus.file.id,
                )
            } else {
                await associateProtocolFiles(args.companyId, args.protocolId, fileStatus.file.id)
            }
        }

        // 4. No Rust state to record. The file is in the server-side
        //    raw_files table (created by requestRawFileUpload) and
        //    associated with the protocol step. The frontend's
        //    loadProtocolFiles() will pick it up on the next refresh.
        return 0
    }

    async abortBrowserUpload(_args: AbortBrowserUploadArgs): Promise<void> {
        this.pendingUploads.delete(_args.uploadId)
    }

    async retryUpload(_companyId: string, _protocolId: string, id: number): Promise<void> {
        // Browser mode: `id` is a browserUploads tempId — dead browser
        // uploads are merged into the queue view by useUploadStatusStore,
        // so the activity modal's Retry lands here. Delegate to the
        // coordinator's retry (dynamic import: a static import would
        // cycle uploadStore → this file → coordinator → uploadStore).
        const {retryBrowserUpload} = await import('./uploadCoordinator')
        await retryBrowserUpload(id)
    }

    async retryAllDeadUploads(_companyId: string, _protocolId: string): Promise<number[]> {
        const {useUploadStatusStore} = await import('./useUploadStatusStore')
        const {retryBrowserUpload} = await import('./uploadCoordinator')
        const ids: number[] = []
        useUploadStatusStore.getState().browserUploads.forEach((entry) => {
            if (entry.status === 'dead') {
                ids.push(entry.tempId)
                void retryBrowserUpload(entry.tempId)
            }
        })
        return ids
    }

    async summary(_companyId: string, _protocolId: string): Promise<UploadSummary> {
        return {found: 0, copied: 0, pending: 0, uploading: 0, persisted: 0, dead: 0}
    }

    async getPendingUploads(_args: GetPendingUploadsArgs): Promise<PendingUpload[]> {
        // Browser mode has no durable queue; return an empty array so the
        // 1-second polling doesn't spam the console with errors. The UI
        // shows server-side files via loadProtocolFiles() in this mode.
        return []
    }

    async getDataCollectionStatus(_companyId: string, _protocolId: string): Promise<DataCollectionStatus> {
        return {watcherRunning: false, uploadWorkerRunning: false, watcherConfig: null}
    }

    async startUploadWorker(_args: StartUploadWorkerConfig): Promise<void> {
        // No-op in browser mode: there's no Rust upload worker to start.
        // The browser-direct upload path (file picks via the dropzone) is
        // handled directly by uploadCoordinator.uploadFileInBrowser +
        // BrowserUploadStore.finalizeBrowserUpload.
    }

    async stopUploadWorker(_companyId: string, _protocolId: string): Promise<void> {
        // No-op in browser mode.
    }

    async startDataCollection(_args: StartDataCollectionConfig): Promise<void> {
        // No-op in browser mode: there's no Rust watcher to start.
        // File system monitoring is a Rust-side feature.
    }

    async stopDataCollection(_companyId: string, _protocolId: string): Promise<void> {
        // No-op in browser mode.
    }
}
